import React, { useMemo } from 'react';

import { OpinionList, ScaleRoot } from './scale.styles';
import {
  OpinionScaleConfig,
  SCALE_END_LIMITS,
  npsTone,
  resolveOpinionScaleConfig,
} from './scaleTypes';
import ScaleLabels from './ScaleLabels';
import { ScaleBlockProps, useScaleValue } from './useScaleValue';

const OpinionScale = React.forwardRef<HTMLInputElement, ScaleBlockProps<OpinionScaleConfig>>(
  (props, ref) => {
    const config = useMemo(
      () => resolveOpinionScaleConfig(props.data?.blockConfig),
      [props.data?.blockConfig],
    );
    const { value, commit, interactive, isLiveForm, showError, hiddenInput } = useScaleValue(
      props,
      ref,
      config.autoAdvance,
    );

    const start = Number(config.start) === 0 ? 0 : 1;
    const end = Math.min(
      SCALE_END_LIMITS.max,
      Math.max(start + 1, Number(config.end) || SCALE_END_LIMITS.max),
    );
    const points = Array.from({ length: end - start + 1 }, (_, i) => start + i);

    return (
      <ScaleRoot className={`scale-block${isLiveForm ? ' is-live' : ''}${showError ? ' has-error' : ''}`}>
        <OpinionList role="radiogroup" $count={points.length}>
          {points.map((point) => (
            <button
              key={point}
              type="button"
              role="radio"
              aria-checked={String(point) === value}
              disabled={!interactive}
              className={`scale-option opinion-point${
                config.colorCoded ? ` tone-${npsTone(point, start, end)}` : ''
              }${String(point) === value ? ' is-selected' : ''}`}
              onClick={() => commit(String(point), config.autoAdvance)}
            >
              {point}
            </button>
          ))}
        </OpinionList>
        <ScaleLabels low={config.lowLabel} mid={config.midLabel} high={config.highLabel} />
        {showError && <div className="scale-error-text">This field is required.</div>}
        <input {...hiddenInput} />
      </ScaleRoot>
    );
  },
);

OpinionScale.displayName = 'OpinionScale';

export default OpinionScale;
